import CourseEditorExpandCard from './CourseEditorExpandCard'
import CourseContentSelector from './CourseContentSelector'
import { v4 as uuid } from 'uuid'
import CourseEditableContent from './CourseEditableContent'
import {
  CourseContent,
  CoursePart,
  jsonCopy,
  LucideIconName,
} from '@bm-js/atlascloud-shared'
import { ChangeEventHandler } from 'react'
import { Icon } from '@bm-js/atlascloud-ui'

type Props = {
  part: CoursePart
  parts: CoursePart[]
  setParts: (parts: CoursePart[]) => void
  index: number
}

const CourseEditorPart = ({ part, parts, setParts, index }: Props) => {
  const removePart = (i: number) => {
    const updated = [...parts]
    updated.splice(i, 1)
    setParts(updated)
  }

  const movePart = (i: number, isUp: boolean) => {
    const updated = [...parts]
    const [reordered] = updated.splice(i, 1)
    const toIndex = isUp ? i - 1 : i + 1
    updated.splice(toIndex, 0, reordered)
    setParts(updated)
  }

  const setPart = (part: CoursePart) => {
    const updated = [...parts]
    updated[index] = part
    setParts(updated)
  }

  const handleChangeName: ChangeEventHandler<HTMLInputElement> = (e) => {
    setPart({
      ...part,
      name: e.target.value,
    })
  }

  const setContentData = (
    data: CourseContent['data'],
    contentIndex: number
  ) => {
    const updated = [...part.contents]
    updated[contentIndex].data = data
    setPart({
      ...part,
      contents: updated,
    })
  }

  const addContent = (contentType: CourseContent) => {
    const updated = [...part.contents]
    updated.push({ ...jsonCopy(contentType), uuid: uuid() })
    setPart({
      ...part,
      contents: updated,
    })
  }

  const deleteContent = (contentIndex: number) => {
    const updated = [...part.contents]
    updated.splice(contentIndex, 1)
    setPart({
      ...part,
      contents: updated,
    })
  }

  const moveContentDown = (contentIndex: number) => {
    if (contentIndex >= part.contents.length - 1) return
    const updated = [...part.contents]
    const [reordered] = updated.splice(contentIndex, 1)
    updated.splice(contentIndex + 1, 0, reordered)
    setPart({
      ...part,
      contents: updated,
    })
  }

  const moveContentUp = (contentIndex: number) => {
    if (contentIndex === 0) return
    const updated = [...part.contents]
    const [reordered] = updated.splice(contentIndex, 1)
    updated.splice(contentIndex - 1, 0, reordered)
    setPart({
      ...part,
      contents: updated,
    })
  }

  return (
    <CourseEditorExpandCard
      header={
        <div className='relative flex grow'>
          <input
            value={part.name}
            onChange={handleChangeName}
            className='w-full bg-transparent outline-none border-none'
          />
          <Icon
            name={LucideIconName.Pen}
            className={'pointer-events-none absolute top-[10px] right-2 size-5'}
          />
        </div>
      }
      extraActions={
        <>
          {parts.length > 1 && (
            <Icon
              name={LucideIconName.Trash2}
              onClick={() => removePart(index)}
              className='cursor-pointer size-5'
            />
          )}
          {index !== 0 && (
            <Icon
              name={LucideIconName.CircleArrowUp}
              onClick={() => movePart(index, true)}
              className='cursor-pointer size-5'
            />
          )}
          {index !== parts.length - 1 && (
            <Icon
              name={LucideIconName.CircleArrowDown}
              onClick={() => movePart(index, false)}
              className='cursor-pointer size-5'
            />
          )}
        </>
      }
    >
      {part.contents.map((content, contentIndex) => (
        <CourseEditableContent
          key={content.uuid}
          moveContentUp={() => moveContentUp(contentIndex)}
          moveContentDown={() => moveContentDown(contentIndex)}
          deleteContent={() => deleteContent(contentIndex)}
          setContentData={(data) => setContentData(data, contentIndex)}
          content={content}
        />
      ))}
      <CourseContentSelector addContent={addContent} />
    </CourseEditorExpandCard>
  )
}

export default CourseEditorPart
